import { getApiBaseUrl } from '@/core/utils/apiUtils';
import { createCookie, getCookie } from '@/core/utils/cookieUtils';
import {
  getCommonServerAttributes,
  getPageName,
  getUserInfo,
} from '@/shared/features/analytics/utils/commonAttributes';

declare global {
  interface Window {
    fbq?: FbqFn;
    _connecto?: { getAnonymousId?: () => string };
  }
}

export interface FbqFn {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
}

/** Max time to wait for the real Meta Pixel SDK before firing anyway (fbp/fbc may be missing). */
const FACEBOOK_SDK_WAIT_TIMEOUT_MS = 2000;
const FACEBOOK_SDK_POLL_MS = 50;

/**
 * Stub-only pixel (installed synchronously by the inline snippet) only queues events;
 * the real SDK attaches `callMethod` once `fbevents.js` has actually loaded & executed.
 * `fbq.loaded` is set by the stub itself and must NOT be used as an SDK-ready check.
 */
export function isFacebookPixelSdkLoaded(): boolean {
  const fbq = window.fbq;
  return typeof fbq === 'function' && typeof fbq.callMethod === 'function';
}

/**
 * Resolves `true` once the real Meta Pixel SDK has loaded, or `false` if `timeoutMs`
 * elapses first (e.g. pixel blocked by an ad-blocker / CSP) so callers can still
 * proceed rather than queuing events forever.
 */
export function waitForFacebookPixelSdk(timeoutMs: number = FACEBOOK_SDK_WAIT_TIMEOUT_MS): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (isFacebookPixelSdkLoaded()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      if (intervalId != null) clearInterval(intervalId);
      if (timeoutId != null) clearTimeout(timeoutId);
      resolve(value);
    };

    intervalId = setInterval(() => {
      if (isFacebookPixelSdkLoaded()) finish(true);
    }, FACEBOOK_SDK_POLL_MS);

    timeoutId = setTimeout(() => finish(false), timeoutMs);
  });
}

export type FacebookServerLogger = (payload: Record<string, unknown>) => void;

let facebookServerLogger: FacebookServerLogger | null = null;

/** Register eventLogger callback from analytics init (optional; falls back to direct fetch). */
export function setFacebookServerLogger(logger: FacebookServerLogger | null): void {
  facebookServerLogger = logger;
}

function getCurrentPageType(): string {
  const pageNameAttr = getPageName();
  return pageNameAttr['Page Name'] || '';
}

export function generateEventId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function removeEmptyParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

/** Meta `content_ids` / `content_names` must always be `string[]`, including a single value. */
export function toFacebookStringArray(value: unknown): string[] {
  if (value == null || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .filter((item) => item != null && item !== '')
    .map(String);
}

/** @deprecated Use `toFacebookStringArray`. */
export function toFacebookContentIds(value: unknown): string[] {
  return toFacebookStringArray(value);
}

function withNormalizedContentArrays(
  eventAttributes: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...eventAttributes };

  if ('content_ids' in next) {
    next.content_ids = toFacebookStringArray(next.content_ids);
  }

  if ('content_names' in next || 'content_name' in next) {
    next.content_names = toFacebookStringArray(next.content_names ?? next.content_name);
    delete next.content_name;
  }

  return next;
}

export function normalizePhoneForFacebookEvents(phone: unknown): string | null {
  if (!phone) return null;
  let normalized = String(phone).trim();
  normalized = normalized.replace(/[+\-\s()]/g, '');
  if (/^\d{10}$/.test(normalized)) {
    normalized = `91${normalized}`;
  }
  return normalized || null;
}

export function getFirstNameForFacebook(userInfo: ReturnType<typeof getUserInfo>): string | null {
  if (!userInfo) return null;
  const first =
    typeof userInfo.firstName === 'string' && userInfo.firstName.trim()
      ? userInfo.firstName.trim()
      : '';
  const middle =
    typeof userInfo.middleName === 'string' && userInfo.middleName.trim()
      ? ` ${userInfo.middleName.trim()}`
      : '';
  const last =
    typeof userInfo.lastName === 'string' && userInfo.lastName.trim()
      ? userInfo.lastName.trim()
      : '';
  const fullName =
    (typeof userInfo.customerName === 'string' && userInfo.customerName.trim()) ||
    (typeof userInfo.userName === 'string' && userInfo.userName.trim()) ||
    '';

  // user-context cache used to only keep customerName; never send that as fn.
  let firstName = first;
  if (firstName && fullName && firstName === fullName) {
    firstName =
      last && firstName.toLowerCase().endsWith(` ${last.toLowerCase()}`)
        ? firstName.slice(0, firstName.length - last.length).trim()
        : '';
  }

  return (firstName + middle).trim() || null;
}

function getDeliveryCity(userInfo: ReturnType<typeof getUserInfo>): string | null {
  if (userInfo?.deliveryCity && typeof userInfo.deliveryCity === 'string') {
    return userInfo.deliveryCity.trim() || null;
  }
  if (userInfo?.cityName && typeof userInfo.cityName === 'string') {
    return userInfo.cityName.trim() || null;
  }
  return null;
}

/**
 * `_fbp` is normally written by Meta's own pixel script (`fbevents.js`), but that
 * script attaches `callMethod` (our "SDK loaded" signal) *before* it finishes its
 * own async config fetch and actually sets the cookie — so waiting for SDK-load
 * alone doesn't guarantee `_fbp` exists yet, especially for the very first event.
 *
 * Meta documents self-generating `_fbp` in this exact format as the recommended
 * fix for this race: `fb.<subdomain_index>.<creation_time_ms>.<random_number>`.
 * If `fbevents.js` later runs and finds a valid existing `_fbp`, it reuses it
 * rather than overwriting it, so this is safe to do unconditionally.
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 */
export function ensureFacebookBrowserId(): string {
  const existing = getCookie('_fbp');
  if (existing) return existing;

  const subdomainIndex = 1; // 1 = cookie set on the exact domain (not a subdomain-shared one)
  const generated = `fb.${subdomainIndex}.${Date.now()}.${Math.floor(Math.random() * 2147483647)}`;
  createCookie('_fbp', generated, 90); // ~90 days, matches Meta's own _fbp expiry
  return generated;
}

export function getConnectoId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window._connecto?.getAnonymousId?.() ?? '';
  } catch {
    return '';
  }
}

export function getFacebookCommonAttributes(
  customAttributes?: Record<string, unknown>,
): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return { ...customAttributes };
  }

  const userInfo = getUserInfo();
  const isLoggedIn = userInfo?.isLoggedIn === true;

  const external_id =
    isLoggedIn && typeof userInfo?.userCode === 'string' && userInfo.userCode.trim()
      ? userInfo.userCode.trim()
      : null;
  const em =
    isLoggedIn && typeof userInfo?.email === 'string'
      ? userInfo.email.trim().toLowerCase()
      : null;
  const ph =
    isLoggedIn && userInfo?.phoneNumber
      ? normalizePhoneForFacebookEvents(userInfo.phoneNumber)
      : null;

  const fn = isLoggedIn ? getFirstNameForFacebook(userInfo) : null;
  const ln =
    isLoggedIn && typeof userInfo?.lastName === 'string' && userInfo.lastName.trim()
      ? userInfo.lastName.trim()
      : null;

  const ct = isLoggedIn && typeof userInfo?.cityName === 'string' ? userInfo.cityName : null;
  const st = isLoggedIn && typeof userInfo?.state === 'string' ? userInfo.state : null;
  const zp =
    userInfo?.guessedPincode === true || !userInfo?.pincode
      ? null
      : String(userInfo.pincode);
  // Meta CAPI `country` must be ISO 3166-1 alpha-2 (lowercase). Always India.
  const country = 'in';

  const commonAttributes: Record<string, unknown> = {
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: window.location.href,
    fbp: ensureFacebookBrowserId(),
    fbc: getCookie('_fbc'),
    external_id,
    em,
    ph,
    fn,
    ln,
    ct,
    st,
    zp,
    country,
    currency: 'INR',
    source_surface: 'website',
    producer_system: 'web_pixel',
    surface_identifier: window.location.pathname,
    surface_title: document.title,
    surface_type: getCurrentPageType(),
    city: getDeliveryCity(userInfo)
  };

  return {
    ...commonAttributes,
    ...customAttributes,
  };
}

function resolveEventLoggerBaseUrl(): string | null {
  const explicit = import.meta.env.VITE_EVENT_LOGGER_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, '');

  const pageApi = import.meta.env.VITE_PAGE_API_BASE_URL?.trim();
  if (pageApi) return pageApi.replace(/\/page\/?$/, '');

  const apiBase = getApiBaseUrl();
  if (apiBase) return apiBase.replace(/\/$/, '');

  return null;
}

function toFormValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function toFormBody(payload: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    params.append(key, toFormValue(value));
  }
  return params.toString();
}

function getClientIpAddressForFacebook(): string | null {
  const ip = getUserInfo()?.client_ip;
  if (typeof ip === 'string' && ip.trim()) return ip.trim();
  return null;
}

function logFacebookServerEvent(eventName: string, eventAttributes: Record<string, unknown>): void {
  const { contents, ...restAttributes } = eventAttributes;
  const payload = removeEmptyParams({
    _eventId: eventName,
    metaEvent: true,
    ...getFacebookCommonAttributes(),
    client_user_agent: navigator.userAgent,
    client_ip_address: getClientIpAddressForFacebook(),
    ...restAttributes,
    ...(typeof contents === 'string'
      ? { contents }
      : contents != null && !(Array.isArray(contents) && contents.length === 0)
        ? { contents: toFormValue(contents) }
        : null),
  });

  if (facebookServerLogger) {
    facebookServerLogger(payload);
    return;
  }

  const baseUrl = resolveEventLoggerBaseUrl();
  if (!baseUrl || typeof window === 'undefined') return;

  const mergedPayload = {
    ...getCommonServerAttributes(),
    ...payload,
  };

  void fetch(`${baseUrl}/eventlogger/server/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: toFormBody(mergedPayload),
    credentials: 'include',
  }).catch(() => {
    // Fire-and-forget � legacy parity.
  });
}

/** Used by optional server-attribution module (ViewContent, etc.). */
export function postFacebookMetaServerEvent(
  eventName: string,
  eventAttributes: Record<string, unknown>,
): void {
  logFacebookServerEvent(eventName, eventAttributes);
}

function logFacebookClientEvent(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): void {
  const payload = removeEmptyParams({
    ...getFacebookCommonAttributes(),
    ...eventAttributes
  });

  const track = (): boolean => {
    const fbq = window.fbq;
    if (typeof fbq !== 'function') return false;
    try {
      fbq('track', eventName, payload, { eventID: eventId });
      console.log('client event triggered...', eventName, payload, { eventID: eventId });
      return true;
    } catch (error) {
      console.error(`Facebook Pixel track failed for '${eventName}':`, error);
      return false;
    }
  };

  if (track()) return;

  setTimeout(() => {
    if (!track()) {
      console.warn(`Facebook Pixel is still not loaded. Event '${eventName}' was skipped.`);
    }
  }, 1000);
}

export interface CommonFacebookEventsOptions {
  /** Skip server-side meta eventLogger POST */
  skipServer?: boolean;
}

/** Blocks Strict Mode remounts and loading-state re-runs from sending the same view twice. */
const recentFacebookEventKeys = new Map<string, number>();
const FACEBOOK_EVENT_DEDUPE_MS = 2000;

export function wasFacebookEventRecentlyFired(key: string, windowMs = FACEBOOK_EVENT_DEDUPE_MS): boolean {
  const now = Date.now();
  const previous = recentFacebookEventKeys.get(key);
  if (previous != null && now - previous < windowMs) return true;
  recentFacebookEventKeys.set(key, now);
  return false;
}

function pageViewDedupeKey(eventName: string, eventAttributes: Record<string, unknown>): string | null {
  if (eventName === 'PageView') {
    return `PageView:${window.location.pathname}`;
  }
  if (eventName === 'ViewContent') {
    const contentIds = toFacebookStringArray(eventAttributes.content_ids);
    const designId = eventAttributes.design_id != null ? String(eventAttributes.design_id) : '';
    const identity = contentIds[0] || designId;
    return identity ? `ViewContent:${identity}` : null;
  }
  if (eventName === 'FindLocation') {
    const storeIdsRaw = eventAttributes.store_ids;
    if (typeof storeIdsRaw === 'string' && storeIdsRaw.trim()) {
      try {
        const parsed = JSON.parse(storeIdsRaw) as unknown;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return `FindLocation:${parsed.map(String).join(',')}`;
        }
      } catch {
        return `FindLocation:${storeIdsRaw}`;
      }
    }
    const city = typeof eventAttributes.store_city === 'string' ? eventAttributes.store_city.trim() : '';
    return city ? `FindLocation:city:${city}` : 'FindLocation:empty';
  }
  return null;
}

/** Temporary rollout gate � only listed events fire. Remove to re-enable all events. */
const ENABLED_FACEBOOK_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'AddToCart',
  'AddToWishlist',
  'CustomizeProduct',
  'FindLocation',
  'Search',
  'CompleteRegistration',
]);

const EXCLUDED_FACEBOOK_EVENT_HOSTS = [
  'oms.bluestone.com',
  'pos.bluestone.com',
  'warmup.bluestone.com',
];

/**
 * Shared guard + payload prep for Meta events. Returns null when dispatch should be skipped.
 * `overrideEventId` lets callers (e.g. the Facebook adapter, for `PageView`) reuse an
 * `event_id` that was already generated for this same domain event/call, so WebEngage
 * (sent that same properties object) ends up with the identical id.
 */
export function prepareFacebookMetaEventDispatch(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
  overrideEventId?: string,
): { eventId: string; attributes: Record<string, unknown> } | null {
  if (typeof window === 'undefined') return null;
  if (EXCLUDED_FACEBOOK_EVENT_HOSTS.includes(window.location.hostname)) return null;
  if (!ENABLED_FACEBOOK_EVENTS.has(eventName)) return null;

  const dedupeKey = pageViewDedupeKey(eventName, eventAttributes);
  if (dedupeKey && wasFacebookEventRecentlyFired(dedupeKey)) return null;

  const eventId = overrideEventId?.trim() || generateEventId();
  const attributes = withNormalizedContentArrays({
    ...eventAttributes
  });

  // TEMPORARY: mirror `ViewContent` (triggered by `PDP Page Viewed` or `ViewContent`) to a
  // new WebEngage `ViewContent` event with the identical `event_id`, purely client-side —
  // unrelated to whichever dispatch style (client pixel / server CAPI) runs after this.
  // Kept here (rather than in usePdpAnalytics.ts) so the existing `PDP Page Viewed`
  // WebEngage event stays completely untouched. `PageView` doesn't need this: it already
  // reaches WebEngage via `trackPageView.ts`'s own `event_id`-carrying properties.
  if (eventName === 'ViewContent') {
    mirrorMetaEventToWebEngage('ViewContent', attributes, eventId);
  }

  return { eventId, attributes };
}

/**
 * Meta event dispatcher: client pixel + server metaEvent.
 */
export function commonFacebookEventsUtil(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
  options?: CommonFacebookEventsOptions,
  overrideEventId?: string,
): void {
  const prepared = prepareFacebookMetaEventDispatch(eventName, eventAttributes, overrideEventId);
  if (!prepared) return;

  const { eventId, attributes } = prepared;
  logFacebookClientEvent(eventName, attributes, eventId);

  if (!options?.skipServer) {
    logFacebookServerEvent(eventName, {
      event_id: eventId ,...attributes
    });
  }
}

/**
 * TEMPORARY: mirrors a Meta event to WebEngage with the exact same `event_id` Meta
 * received, inlined right next to the Meta dispatch call sites so no unrelated code
 * (e.g. usePdpAnalytics.ts) needs to change. Bypasses `WebenageAdapter` entirely.
 * Remove once WebEngage event_id sharing gets a permanent, adapter-level design.
 */
export function mirrorMetaEventToWebEngage(
  eventName: string,
  attributes: Record<string, unknown>,
  eventId: string,
): void {
  if (typeof window === 'undefined' || !window.webengage) return;
  try {
    window.webengage.track(eventName, { ...attributes, event_id: eventId });
  } catch (error) {
    console.error(`Error tracking '${eventName}' to WebEngage:`, error);
  }
}

/** @deprecated Prefer commonFacebookEventsUtil for full client+server parity. */
export function trackFacebookEvent(
  eventName: string,
  customAttributes?: Record<string, unknown>,
): void {
  commonFacebookEventsUtil(eventName, customAttributes ?? {});
}
